/**
 * GroupBuilderTour — guided demo tour for the "Onboard & group
 * compressors" wizard. While the tour runs the host page injects the
 * DEMO_* mock data below so all 4 wizard steps can be previewed
 * end-to-end without touching the Control Plane; everything is
 * cleared when the tour ends.
 */
import { useState } from "react";
import { useT } from "@/lib/i18n";
import ControlledSeqTour from "./ControlledSeqTour";

const STORAGE_KEY = "tour_seq_group_builder_v1";

export const DEMO_TENANT_ID = "demo-tenant";
export const DEMO_SERIALS = ["DEMO-1001", "DEMO-1002"];

export const DEMO_ITEMS = [
  {
    serial_number: "DEMO-1001", display_serial: "AC-1001",
    owner_company_id: DEMO_TENANT_ID,
    owner_company_name: "Acme Manufacturing (example)",
    owner_city: "Toronto", owner_state: "ON",
    product_name: "GA 75 VSD", compressor_hours: 12450,
    plc_type: "ES4000", inverter: { type: "VSD" }, cp_status: "new",
  },
  {
    serial_number: "DEMO-1002", display_serial: "AC-1002",
    owner_company_id: DEMO_TENANT_ID,
    owner_company_name: "Acme Manufacturing (example)",
    owner_city: "Toronto", owner_state: "ON",
    product_name: "GA 55", compressor_hours: 8210,
    plc_type: "ES4000", cp_status: "new",
  },
  {
    serial_number: "DEMO-1003", display_serial: "AC-1003",
    owner_company_id: DEMO_TENANT_ID,
    owner_company_name: "Acme Manufacturing (example)",
    owner_city: "Toronto", owner_state: "ON",
    product_name: "GA 37", compressor_hours: 15900,
    plc_type: "ES3000", cp_status: "new",
  },
];

export const DEMO_TEMPLATES = [
  {
    id: "demo-map-1", name: "ES4000 Standard (example)",
    device_template_external_id: "tpl-es4000-std",
    notes: "Example mapping shown during the guided tour.",
  },
];

export const DEMO_MEMBER_META = {
  "DEMO-1001": {
    plc_type: "ES4000", inverter_type: "VSD", product_name: "GA 75 VSD",
    suggested_template_id: "tpl-es4000-std", suggested_map_id: "demo-map-1",
    cp_state: { tenant: false, site: false, device: false },
  },
  "DEMO-1002": {
    plc_type: "ES4000", product_name: "GA 55",
    suggested_template_id: "tpl-es4000-std", suggested_map_id: "demo-map-1",
    cp_state: { tenant: false, site: false, device: false },
  },
};

export const DEMO_OVERRIDES = {
  "DEMO-1001": { device_template_external_id: "tpl-es4000-std", template_map_id: "demo-map-1" },
  "DEMO-1002": { device_template_external_id: "tpl-es4000-std", template_map_id: "demo-map-1" },
};

export const DEMO_BUILD_RESULT = {
  ok: true,
  group_id: null,
  audit: [
    { step: "tenant", ok: true, external_id: DEMO_TENANT_ID },
    { step: "site",   ok: true, external_id: `site-${DEMO_TENANT_ID}-hq` },
    { step: "device", ok: true, external_id: "DEMO-1001" },
    { step: "device", ok: true, external_id: "DEMO-1002" },
    { step: "group",  ok: true, external_id: "grp-example" },
  ],
  onboarded: {
    "DEMO-1001": {
      device_template_external_id: "tpl-es4000-std",
      cp_device_id: "dev-demo-1001",
      tx_topic: `v1/${DEMO_TENANT_ID}/site-${DEMO_TENANT_ID}-hq/DEMO-1001/tx`,
      rx_topic: `v1/${DEMO_TENANT_ID}/site-${DEMO_TENANT_ID}-hq/DEMO-1001/rx`,
    },
    "DEMO-1002": {
      device_template_external_id: "tpl-es4000-std",
      cp_device_id: "dev-demo-1002",
      tx_topic: `v1/${DEMO_TENANT_ID}/site-${DEMO_TENANT_ID}-hq/DEMO-1002/tx`,
      rx_topic: `v1/${DEMO_TENANT_ID}/site-${DEMO_TENANT_ID}-hq/DEMO-1002/rx`,
    },
  },
};

function buildSteps(t) {
  return [
    // ── Wizard step 1: pick compressors ────────────────────────────
    {
      stage: 1,
      target: '[data-tour="builder-header"]',
      content: t("tour.sgb.header",
        "This wizard onboards compressors end-to-end in 4 steps: pick compressors → device templates → group details → MQTT topics. We've loaded EXAMPLE data so you can preview every step — nothing is saved."),
      placement: "bottom",
    },
    {
      stage: 1,
      target: '[data-testid="builder-search-bar"]',
      content: t("tour.sgb.search",
        "Find compressors by serial number or customer name. Filters let you show inverter (VSD) units only, and hide compressors that are already in a group."),
      placement: "bottom",
    },
    {
      stage: 1,
      target: '[data-testid="builder-list"]',
      content: t("tour.sgb.list",
        "Tick the compressors to onboard. All members of a group must belong to the same customer — mixing tenants is blocked."),
      placement: "top",
    },
    {
      stage: 1,
      target: '[data-testid="builder-tenant-banner"]',
      content: t("tour.sgb.tenant",
        "As soon as you pick the first compressor the tenant locks. Clear the selection to switch to a different customer."),
      placement: "bottom",
    },
    {
      stage: 1,
      target: '[data-testid="builder-next-step1"]',
      content: t("tour.sgb.next1",
        "When you're happy with the selection, continue to device types."),
      placement: "top",
    },
    // ── Wizard step 2: templates + transport ───────────────────────
    {
      stage: 2,
      target: '[data-testid="builder-template-list"]',
      content: t("tour.sgb.templates",
        "Each compressor gets a Device template (a reusable register/polling configuration in the Control Plane) and a Communication type. Suggestions are pre-filled from the PLC/inverter mapping."),
      placement: "top",
    },
    {
      stage: 2,
      target: '[data-testid="builder-template-select-DEMO-1001"]',
      content: t("tour.sgb.template_select",
        "Pick the template here. Options marked (suggested) come from the admin-managed PLC → template mapping."),
      placement: "left",
    },
    {
      stage: 2,
      target: '[data-testid="builder-comm-type-DEMO-1001"]',
      content: t("tour.sgb.commtype",
        "MQTT is the default transport. Transparent TCP RTU is for compressors behind a USR-DR404 gateway — extra fields (plus the DR404 USB configurator and WiFi setup) appear when you select it."),
      placement: "left",
    },
    {
      stage: 2,
      target: '[data-testid="builder-next-step2"]',
      content: t("tour.sgb.next2",
        "Every MQTT row needs a template; TCP RTU rows need gateway details instead. Then continue to group details."),
      placement: "top",
    },
    // ── Wizard step 3: group details + review ──────────────────────
    {
      stage: 3,
      target: '[data-testid="builder-group-name"]',
      content: t("tour.sgb.name",
        "Name the group — this is how operators find it later, e.g. 'Plant A — Lead/Lag'."),
      placement: "bottom",
    },
    {
      stage: 3,
      target: '[data-testid="builder-pset"]',
      content: t("tour.sgb.pset",
        "Common Pset is the shared pressure setpoint (bar) the sequencer balances around. Next to it you can override the auto-generated site name."),
      placement: "bottom",
    },
    {
      stage: 3,
      target: '[data-testid="builder-cp-preview"]',
      content: t("tour.sgb.preview",
        "Before anything runs you see the exact Control-Plane calls: tenant → site → one device per compressor. The push is idempotent — re-running upserts the same records."),
      placement: "top",
    },
    {
      stage: 3,
      target: '[data-testid="builder-build"]',
      content: t("tour.sgb.build",
        "Provision & build executes the pushes and creates the group. It's inactive during this demo — with real data it takes a few seconds and reports every step."),
      placement: "top",
    },
    // ── Wizard step 4: result + MQTT topics ────────────────────────
    {
      stage: 4,
      target: '[data-testid="builder-result"]',
      content: t("tour.sgb.result",
        "The audit trail shows each provisioning step. Green = created (or already existed); red rows carry the error so you can fix and retry."),
      placement: "bottom",
    },
    {
      stage: 4,
      target: '[data-testid="builder-mqtt-panel"]',
      content: t("tour.sgb.mqtt",
        "Copy each device's tx/rx MQTT topics into the on-site gateway. Topics stay saved on the device so you can re-fetch them any time from the Live Dashboard. After a real build, continue straight to commissioning!"),
      placement: "top",
    },
  ];
}

export default function GroupBuilderTour({ runFlag, onStart, onStageChange, onStop }) {
  const t = useT();
  const [steps] = useState(() => buildSteps(t));
  return (
    <ControlledSeqTour
      steps={steps}
      storageKey={STORAGE_KEY}
      runFlag={runFlag}
      onStart={onStart}
      onStageChange={onStageChange}
      onStop={onStop}
    />
  );
}
